import {
  Component, OnDestroy, inject,
  ViewChild, TemplateRef,
  computed, effect, signal,
  OnInit,
  ElementRef, HostListener,
} from '@angular/core';
import { CommonModule }     from '@angular/common';
import { FormsModule }      from '@angular/forms';
import { Router }           from '@angular/router';

import { NavLeftService }   from 'src/app/services/nav-left.service';
import { PageBarService }   from 'src/app/services/page-bar.service';
import { TreeNode, TreeNodeEvent } from '@libs/front/components/tree-view/tree-view.component';
import { InputComponent }   from '@libs/front/components/input/input.component';
import { ButtonComponent }  from '@libs/front/components/button/button.component';
import { TreeNodeAutoComplete } from '@libs/front/components/autocomplete/autocomplete.component';
import { HttpService }      from 'src/app/services/http.service';
import { RegionDto }        from 'src/app/bo/region';
import { toSignal }         from '@angular/core/rxjs-interop';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { SitesMapComponent } from './sites-map/sites-map.component';
import { SiteFeature, FlyTarget } from './sites-map/sites-map.types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CartePopupComponent } from '@libs/front/components/popup-panel/popup-panel.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { AreaDashboardComponent } from './dashboard/area-dashboard.component';
import { PlanningComponent } from 'src/app/shared/components/planning/planning.component';
import { FeatureFlagService } from 'src/app/services/new_services/feature-flag.service';

// ── Clés localStorage ─────────────────────────────────────────────────────────
const LS_UNCHECKED_REGIONS  = 'area.uncheckedRegionIds';
const LS_UNCHECKED_SITES    = 'area.uncheckedSiteIds';
const LS_SELECTED_NODE_ID   = 'area.selectedNodeId';
const LS_SELECTED_NODE_TYPE = 'area.selectedNodeType'; // 'region' | 'site'
const LS_FILTER_LEVEL1      = 'area.filterLevel1';
const LS_BLAST_NAME_DISPLAY = 'area.blastNameDisplay'; // 'start' | 'end'

// ── Helpers localStorage ──────────────────────────────────────────────────────

function lsLoadSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set<string>(parsed);
  } catch {}
  return new Set();
}

function lsSaveSet(key: string, set: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {}
}

function lsLoad(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function lsSave(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch {}
}

@Component({
  selector:    'app-area',
  templateUrl: './area.component.html',
  styleUrls:   ['./area.component.scss'],
  standalone:  true,
  imports: [
    CommonModule, FormsModule, TranslateModule,
    InputComponent, ButtonComponent,
    SitesMapComponent, CartePopupComponent,
    SelectComponent, AreaDashboardComponent,
    PlanningComponent,
  ],
})
export class AreaComponent implements OnInit, OnDestroy {

  private navLeftService    = inject(NavLeftService);
  private pageBarService    = inject(PageBarService);
  private http              = inject(HttpService);
  private utilisateurService = inject(UtilisateurService);
  private translate          = inject(TranslateService);
  private featureFlags       = inject(FeatureFlagService);
  public  router            = inject(Router);

  @ViewChild('searchTpl',         { static: true }) searchTpl!:         TemplateRef<any>;
  @ViewChild('actionsTpl',        { static: true }) actionsTpl!:        TemplateRef<any>;
  @ViewChild('pageBarActionsTpl', { static: true }) pageBarActionsTpl!: TemplateRef<any>;
  @ViewChild('filterBtn') private filterBtnRef?: ElementRef<HTMLElement>;

  // ── Données ───────────────────────────────────────────────────────────────
  private rawRegions = toSignal(
    this.http.get<RegionDto[]>('Regions/GetByCurrentUser'),
    { initialValue: [] as RegionDto[] }
  );

  // Sites accessibles à l'utilisateur courant (même source que la nav-bar)
  private userSites = toSignal(
    this.utilisateurService.listeSites$,
    { initialValue: this.utilisateurService.listeSites }
  );

  // Régions filtrées : on ne garde que les sites auxquels l'utilisateur a accès,
  // et on exclut les régions vides après filtrage.
  regions = computed<RegionDto[]>(() => {
    const allowedSiteIds = new Set(this.userSites().map(s => s.siteId));
    return this.rawRegions()
      .map(region => ({
        ...region,
        sites: region.sites.filter(s => allowedSiteIds.has(s.siteId)),
      }))
      .filter(region => region.sites.length > 0);
  });

  sitesFeatures = computed<SiteFeature[]>(() =>
    this.regions().flatMap(region =>
      region.sites.map(site => ({
        id:           site.siteId,
        label:        site.nom,
        area:         region.nom,
        lat:          site.coordY,
        lon:          site.coordX,
        isActive:     site.isActif,
        numberBlasts: site.numberBlasts,
        epsg:         site.epsg,
      }))
    )
  );

  private siteToRegionMap = computed(() => {
    const map = new Map<string, string>();
    for (const region of this.regions()) {
      for (const site of region.sites) {
        map.set(String(site.siteId), String(region.regionId));
      }
    }
    return map;
  });

  filterLevel1Value = signal<SelectOption | null>(null);
  appliedFilter     = signal<SelectOption | null>(null);

  level1Options: SelectOption[] = [
    { id: '',     label: 'None' },
    { id: 'area', label: 'Area' },
  ];

  blastDisplayOptions: SelectOption[] = [
    { id: 'end',   label: 'Truncated at the end' },
    { id: 'start', label: 'Truncated at the start' },
  ];
  blastDisplayValue = signal<SelectOption | null>(null);
  labelEllipsis     = signal<'start' | 'end'>('end');

  treeData = computed(() => {
    const filter = this.appliedFilter()?.id;
    return filter === 'area'
      ? this.mapRegionsToTreeNodes(this.regions())
      : filter === 'site' ? this.mapSitesToTreeNodes(this.regions())
          : this.mapRegionsAndSitesToTreeNodes(this.regions());
  });

  // ── Sélection ─────────────────────────────────────────────────────────────
  selectedRegion        = signal<RegionDto | null>(null);
  selectedRegionSiteIds = signal<(string | number)[]>([]);
  selectedSiteId        = signal<string | number | null>(null);
  flyTo                 = signal<FlyTarget | null>(null);
  closePopupTrigger     = signal(0);

  // ── Filtre panel ──────────────────────────────────────────────────────────
  filterOpen     = signal(false);
  filterPopupPos = signal({ top: '0px', right: '0px' });

  openFilter(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    this.filterPopupPos.set({
      top:   `${rect.bottom + 4}px`,
      right: `${window.innerWidth - rect.right}px`,
    });
    if (!this.filterOpen()) {
      this.filterLevel1Value.set(this.appliedFilter());
    }
    this.filterOpen.set(!this.filterOpen());
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.filterOpen()) return;
    const wrapper = this.filterBtnRef?.nativeElement;
    if (!wrapper) return;
    if (!wrapper.contains(event.target as Node)) {
      this.filterLevel1Value.set(this.appliedFilter());
      this.filterOpen.set(false);
    }
  }

  applyFilter(): void {
    // Confirme le filtre → treeData se recalcule via computed()
    const filter = this.filterLevel1Value();
    this.appliedFilter.set(filter);
    if (filter?.id) {
      lsSave(LS_FILTER_LEVEL1, filter.id);
    } else {
      try { localStorage.removeItem(LS_FILTER_LEVEL1); } catch {}
    }

    // Confirme la position des « ... » → transmise au tree-view via l'effect
    const display   = this.blastDisplayValue();
    const displayId = display?.id === 'start' ? 'start' : 'end';
    this.labelEllipsis.set(displayId);
    lsSave(LS_BLAST_NAME_DISPLAY, displayId);

    this.filterOpen.set(false);
  }

  resetFilter(): void {
    this.filterLevel1Value.set(null);
    this.appliedFilter.set(null);
    try { localStorage.removeItem(LS_FILTER_LEVEL1); } catch {}

    // Réinitialise la position des « ... » au défaut (fin)
    this.blastDisplayValue.set(this.blastDisplayOptions[0]);
    this.labelEllipsis.set('end');
    try { localStorage.removeItem(LS_BLAST_NAME_DISPLAY); } catch {}

    this.filterOpen.set(false);
  }

  // ── Visibilité carte ──────────────────────────────────────────────────────
  private uncheckedRegionIds = signal<Set<string>>(lsLoadSet(LS_UNCHECKED_REGIONS));
  private uncheckedSiteIds   = signal<Set<string>>(lsLoadSet(LS_UNCHECKED_SITES));

  visibleSites = computed<SiteFeature[]>(() => {
    const uncheckedRegions = this.uncheckedRegionIds();
    const uncheckedSites   = this.uncheckedSiteIds();
    const siteToRegion     = this.siteToRegionMap();

    return this.sitesFeatures().filter(site => {
      if (uncheckedSites.has(String(site.id))) return false;
      const regionId = siteToRegion.get(String(site.id));
      if (regionId && uncheckedRegions.has(regionId)) return false;
      return true;
    });
  });

  // ── Recherche / onglet ────────────────────────────────────────────────────
  searchText = signal('');
  activeTab  = signal('maps');

  constructor() {
    // Restaure le filtre Level 1 desde localStorage
    const savedFilterId = lsLoad(LS_FILTER_LEVEL1);
    if (savedFilterId) {
      const opt = this.level1Options.find(o => o.id === savedFilterId) ?? null;
      this.appliedFilter.set(opt);
      this.filterLevel1Value.set(opt);
    }

    // Restaure la position des « ... » depuis localStorage (défaut : fin)
    const savedDisplay = lsLoad(LS_BLAST_NAME_DISPLAY) === 'start' ? 'start' : 'end';
    this.labelEllipsis.set(savedDisplay);
    this.blastDisplayValue.set(
      this.blastDisplayOptions.find(o => o.id === savedDisplay) ?? this.blastDisplayOptions[0]
    );

    effect(() => {
      this.navLeftService.set({
        treeData:         this.treeData(),
        searchText:       this.searchText(),
        labelEllipsis:    this.labelEllipsis(),
        showExpandCollapse: true,
        searchTemplate:   this.searchTpl,
        actionsTemplate:  this.actionsTpl,
        onNodeClick:      (e) => this.onNodeClick(e),
        onBadgeClick:     (e) => this.onBadgeClick(e),
        onCheckboxChange: (e) => this.onCheckboxChange(e),
        onSelectAll:      (checked) => this.onSelectAll(checked),
      });
    });

    effect(() => {
      const regions = this.regions();
      if (!regions.length) return;
      this.restoreSelection(regions);
    });
  }

  ngOnInit(): void {
    this.blastDisplayOptions = [
      { id: 'end',   label: this.translate.instant('AREA.TRUNCATED_AT_END') },
      { id: 'start', label: this.translate.instant('AREA.TRUNCATED_AT_START') },
    ];

    this.pageBarService.set({
      backLabel:       'GLOBAL.USER_List',
      label:           '',
      tabs:            [
        { id: 'maps',      label: 'GLOBAL.CARTE',      dataAttr: 'area-tab-maps' },
        { id: 'dashboard', label: 'GLOBAL.DASHBOARD', dataAttr: 'area-tab-dashboard' },
        { id: 'planning',  label: 'PLANNING.TITLE',  dataAttr: 'area-tab-planning'  },
      ],
      activeTabId:     'maps',
      onTabClick:      (tab) => this.activeTab.set(tab.id),
      actionsTemplate: this.pageBarActionsTpl,
    });
  }

  ngOnDestroy(): void {
    this.navLeftService.reset();
    this.pageBarService.reset();
  }

  // ── Restauration carte + page-bar au chargement ───────────────────────────
  private restoreSelection(regions: RegionDto[]): void {
    const savedRaw  = lsLoad(LS_SELECTED_NODE_ID);
    const savedType = lsLoad(LS_SELECTED_NODE_TYPE);
    if (!savedRaw || !savedType) {
      const allSites = this.sitesFeatures();
      if (allSites.length) this.flyTo.set(this.computeRegionFlyTo(allSites));
      return;
    }
    // Extrait l'id pur depuis le format "type:id" (ex: "region:23" → "23")
    const savedId = savedRaw.includes(':') ? savedRaw.slice(savedRaw.indexOf(':') + 1) : savedRaw;

    if (savedType === 'region') {
      const region  = regions.find(r => String(r.regionId) === savedId);
      if (!region) {
        // Région stockée introuvable → fallback dézoom global
        const allSites = this.sitesFeatures();
        if (allSites.length) this.flyTo.set(this.computeRegionFlyTo(allSites));
        return;
      }
      const siteIds = region.sites.map(s => s.siteId);
      this.selectedRegionSiteIds.set(siteIds);
      this.selectedSiteId.set(null);
      this.selectedRegion.set(region);
      this.pageBarService.setLabel(region.nom);

      const siteIdSet   = new Set(siteIds.map(id => String(id)));
      const regionSites = this.sitesFeatures().filter(s => siteIdSet.has(String(s.id)));
      this.flyTo.set(this.computeRegionFlyTo(regionSites));

    } else if (savedType === 'site') {
      const parentRegion = regions.find(r => r.sites.some(s => String(s.siteId) === savedId));
      if (!parentRegion) {
        // Site stocké introuvable → fallback dézoom global
        const allSites = this.sitesFeatures();
        if (allSites.length) this.flyTo.set(this.computeRegionFlyTo(allSites));
        return;
      }
      this.selectedSiteId.set(savedId);
      this.selectedRegionSiteIds.set(parentRegion.sites.map(s => s.siteId));
      this.selectedRegion.set(parentRegion);
      this.pageBarService.setLabel(parentRegion.nom);

      const site = this.sitesFeatures().find(s => String(s.id) === savedId);
      if (site) {
        this.flyTo.set({ lon: site.lon, lat: site.lat, zoom: 12 });
      }
    }
  }

  // ── Click carte ───────────────────────────────────────────────────────────
  onSiteClicked(site: SiteFeature): void {
    this.router.navigate(this.siteRoute(site.id), { replaceUrl: true });
  }

  /**
   * Le flag actif maintient l'utilisateur sur l'ancien écran site ; sinon il
   * bascule sur le nouveau. Flag inconnu ou API muette : nouveau front.
   */
  private siteRoute(siteId: string | number): (string | number)[] {
    return this.featureFlags.isLegacySiteEnabled(siteId)
      ? ['/mysite', siteId]
      : ['/site', siteId];
  }

  onMarkerSelected(site: SiteFeature): void {
    const siteIdStr    = String(site.id);
    const parentRegion = this.regions().find(r =>
      r.sites.some(s => String(s.siteId) === siteIdStr)
    );

    this.selectedSiteId.set(site.id);
    this.selectedRegionSiteIds.set(parentRegion?.sites.map(s => s.siteId) ?? []);
    this.pageBarService.setLabel(parentRegion?.nom ?? '');

    lsSave(LS_SELECTED_NODE_ID,   `site:${siteIdStr}`);
    lsSave(LS_SELECTED_NODE_TYPE, 'site');

    this.navLeftService.setSelectedNodeId(`site:${siteIdStr}`);
  }

  // ── Fermeture popup carte → désélection site dans le tree ────────────────
  onPopupClosed(): void {
    this.selectedSiteId.set(null);

    const savedRaw  = lsLoad(LS_SELECTED_NODE_ID);
    const savedType = lsLoad(LS_SELECTED_NODE_TYPE);
    // Extrait l'id pur depuis le format "type:id" (ex: "site:58" → "58")
    const savedId = savedRaw && savedRaw.includes(':')
      ? savedRaw.slice(savedRaw.indexOf(':') + 1)
      : (savedRaw ?? '');

    if (savedType === 'site' && savedId) {
      const parentRegion = this.regions().find(r =>
        r.sites.some(s => String(s.siteId) === savedId)
      );
      if (parentRegion) {
        const regionId = String(parentRegion.regionId);
        lsSave(LS_SELECTED_NODE_ID,   `region:${regionId}`);
        lsSave(LS_SELECTED_NODE_TYPE, 'region');
        this.navLeftService.setSelectedNodeId(`region:${regionId}`);
        this.selectedRegionSiteIds.set(parentRegion.sites.map(s => s.siteId));
        this.pageBarService.setLabel(parentRegion.nom);
      } else {
        lsSave(LS_SELECTED_NODE_ID,   '');
        lsSave(LS_SELECTED_NODE_TYPE, '');
        this.navLeftService.setSelectedNodeId('');
        this.selectedRegionSiteIds.set([]);
        this.pageBarService.setLabel('');
      }
    }
  }

  // ── Événements tree-view ──────────────────────────────────────────────────
  onNodeClick(event: TreeNodeEvent): void {
    this.closePopupTrigger.update(n => n + 1);
    if (event.type === 'region') {
      const region  = this.regions().find(r => r.regionId === event.id);
      const siteIds = region?.sites.map(s => s.siteId) ?? [];
      this.selectedRegionSiteIds.set(siteIds);
      this.selectedRegion.set(region ?? null);
      this.pageBarService.setLabel(region?.nom ?? '');
      this.selectedSiteId.set(null);

      lsSave(LS_SELECTED_NODE_ID,   `region:${String(event.id)}`);
      lsSave(LS_SELECTED_NODE_TYPE, 'region');

      const siteIdSet   = new Set(siteIds.map(id => String(id)));
      const regionSites = this.sitesFeatures().filter(s => siteIdSet.has(String(s.id)));
      this.flyTo.set(this.computeRegionFlyTo(regionSites));

    } else if (event.type === 'site') {
      this.selectedSiteId.set(event.id);

      const parentRegion = this.regions().find(r => r.sites.some(s => s.siteId === event.id));
      this.pageBarService.setLabel(parentRegion?.nom ?? '');
      this.selectedRegionSiteIds.set(parentRegion?.sites.map(s => s.siteId) ?? []);
      this.selectedRegion.set(parentRegion ?? null);

      lsSave(LS_SELECTED_NODE_ID,   `site:${String(event.id)}`);
      lsSave(LS_SELECTED_NODE_TYPE, 'site');

      const site = this.sitesFeatures().find(s => s.id === event.id);
      if (site) {
        this.flyTo.set({ lon: site.lon, lat: site.lat, zoom: 12 });
      }
    }
  }

  onBadgeClick(event: TreeNodeEvent): void {
    this.router.navigate(this.siteRoute(event.id), { replaceUrl: true });
  }

  // ── Checkbox ──────────────────────────────────────────────────────────────
  onCheckboxChange(event: TreeNodeEvent): void {
    const id      = String(event.id);
    const checked = event.checked ?? true;

    if (event.type === 'region') {
      this.uncheckedRegionIds.update(set => {
        const next = new Set(set);
        checked ? next.delete(id) : next.add(id);
        lsSaveSet(LS_UNCHECKED_REGIONS, next);
        return next;
      });

      const childSiteIds = this.regions()
        .find(r => String(r.regionId) === id)
        ?.sites.map(s => String(s.siteId)) ?? [];

      if (childSiteIds.length) {
        this.uncheckedSiteIds.update(set => {
          const next = new Set(set);
          childSiteIds.forEach(siteId => checked ? next.delete(siteId) : next.add(siteId));
          lsSaveSet(LS_UNCHECKED_SITES, next);
          return next;
        });
      }

    } else if (event.type === 'site') {
      this.uncheckedSiteIds.update(set => {
        const next = new Set(set);
        checked ? next.delete(id) : next.add(id);
        lsSaveSet(LS_UNCHECKED_SITES, next);
        return next;
      });

      if (checked) {
        const parentRegion = this.regions().find(r =>
          r.sites.some(s => String(s.siteId) === id)
        );
        if (parentRegion) {
          this.uncheckedRegionIds.update(set => {
            const next = new Set(set);
            next.delete(String(parentRegion.regionId));
            lsSaveSet(LS_UNCHECKED_REGIONS, next);
            return next;
          });
        }
      }
    }
  }

  onSelectAll(checked: boolean): void {
    if (checked) {
      const empty = new Set<string>();
      this.uncheckedRegionIds.set(new Set(empty));
      this.uncheckedSiteIds.set(new Set(empty));
      lsSaveSet(LS_UNCHECKED_REGIONS, empty);
      lsSaveSet(LS_UNCHECKED_SITES, empty);
      return;
    }

    const regions     = this.regions();
    const allRegionIds = new Set(regions.map(r => String(r.regionId)));
    const allSiteIds   = new Set(regions.flatMap(r => r.sites.map(s => String(s.siteId))));
    this.uncheckedRegionIds.set(allRegionIds);
    this.uncheckedSiteIds.set(allSiteIds);
    lsSaveSet(LS_UNCHECKED_REGIONS, allRegionIds);
    lsSaveSet(LS_UNCHECKED_SITES, allSiteIds);
  }

  onSiteSelected(node: TreeNodeAutoComplete): void {
    this.searchText.set(node.label);
  }

  // ── Calcul FlyTo pour une région ─────────────────────────────────────────
  private computeRegionFlyTo(sites: SiteFeature[]): FlyTarget | null {
    // Exclut les sites aux coordonnées hors plage WGS84 (coords locales sans EPSG)
    // sinon min/max dérive et le flyTo est rejeté côté carte par isValidLonLat.
    const valid = sites.filter(s =>
      isFinite(s.lon) && isFinite(s.lat)
      && s.lon >= -180 && s.lon <= 180
      && s.lat >= -90  && s.lat <= 90
    );
    if (!valid.length) return null;
    if (valid.length === 1) return { lon: valid[0].lon, lat: valid[0].lat, zoom: 12 };

    const lons      = valid.map(s => s.lon);
    const lats      = valid.map(s => s.lat);
    const minLon    = Math.min(...lons);
    const maxLon    = Math.max(...lons);
    const minLat    = Math.min(...lats);
    const maxLat    = Math.max(...lats);
    const centerLon = (minLon + maxLon) / 2;
    const centerLat = (minLat + maxLat) / 2;
    const maxDelta  = Math.max(maxLon - minLon, maxLat - minLat) || 0.0001;
    const zoom      = Math.max(3, Math.min(12, Math.round(Math.log2(360 / maxDelta)) - 1));

    return { lon: centerLon, lat: centerLat, zoom };
  }

  // ── Mapping tree — mode normal (région → sites) ───────────────────────────
  private mapRegionsAndSitesToTreeNodes(regions: RegionDto[]): TreeNode[] {
    const uncheckedRegions = lsLoadSet(LS_UNCHECKED_REGIONS);
    const uncheckedSites   = lsLoadSet(LS_UNCHECKED_SITES);

    return regions.sort((a, b) => a.nom.localeCompare(b.nom)).map(region => ({
      id:      region.regionId,
      type:    'region',
      label:   region.nom,
      checked: !uncheckedRegions.has(String(region.regionId)),
      children: region.sites.sort((a, b) => a.nom.localeCompare(b.nom)).map(site => ({
        id:      site.siteId,
        type:    'site',
        label:   site.nom,
        badge:   site.isActif ? 'TREEVIEW.OPEN' : undefined,
        checked: !uncheckedSites.has(String(site.siteId)),
      })),
    }));
  }

  private mapRegionsToTreeNodes(regions: RegionDto[]): TreeNode[] {
    const uncheckedRegions = lsLoadSet(LS_UNCHECKED_REGIONS);

    return regions.sort((a, b) => a.nom.localeCompare(b.nom)).map(region => ({
      id:      region.regionId,
      type:    'region',
      label:   region.nom,
      checked: !uncheckedRegions.has(String(region.regionId))
    }));
  }

  // ── Mapping tree — mode plat (Level 1 = Area) ─────────────────────────────
  // Affiche uniquement les sites, triés alphabétiquement, sans nœud région.
  private mapSitesToTreeNodes(regions: RegionDto[]): TreeNode[] {
    const uncheckedSites = lsLoadSet(LS_UNCHECKED_SITES);

    return regions
      .flatMap(region =>
        region.sites.map(site => ({
          id:      site.siteId,
          type:    'site',
          label:   site.nom,
          badge:   site.isActif ? 'TREEVIEW.OPEN' : undefined,
          checked: !uncheckedSites.has(String(site.siteId)),
        }))
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }
}